import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, styled, alpha } from '@mui/material';

// Styled Dialog wrapper with modern design system styling
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.6),
    backdropFilter: 'blur(8px)',
  },
  '& .MuiPaper-root': {
    borderRadius: '24px',
    background: `linear-gradient(150deg, ${alpha(
      theme.palette.background.paper,
      0.95
    )} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)',
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.3)}, transparent)`,
    },
  },
  '& .MuiDialogTitle-root': {
    padding: theme.spacing(3, 3, 2),
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(4),
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
    overflow: 'visible',
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2, 3, 3),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    background: `linear-gradient(135deg, transparent 0%, ${alpha(theme.palette.background.default, 0.3)} 100%)`,
    '& .MuiButton-root': {
      borderRadius: '12px',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.95rem',
      padding: theme.spacing(1, 2.5),
      minWidth: '100px',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-1px)',
      },
    },
    '& .MuiButton-containedPrimary': {
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      color: theme.palette.primary.contrastText,
      boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
      '&:hover': {
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: `0 6px 20px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
      },
    },
    '& .MuiButton-outlined': {
      border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
      color: theme.palette.text.primary,
      background: alpha(theme.palette.background.paper, 0.5),
      backdropFilter: 'blur(10px)',
      '&:hover': {
        border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        background: alpha(theme.palette.primary.main, 0.05),
      },
    },
    '& .MuiButton-containedError': {
      background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
      color: theme.palette.error.contrastText,
      boxShadow: `0 4px 14px 0 ${alpha(theme.palette.error.main, 0.3)}`,
      '&:hover': {
        background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`,
        boxShadow: `0 6px 20px 0 ${alpha(theme.palette.error.main, 0.4)}`,
      },
    },
  },
}));

// Styled Dialog Content for modern form elements
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  '& .MuiFormControl-root': {
    marginBottom: 0,
  },
  '& .MuiTextField-root': {
    '& .MuiInputBase-root': {
      borderRadius: '12px',
      background: alpha(theme.palette.background.paper, 0.5),
      backdropFilter: 'blur(10px)',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: alpha(theme.palette.background.paper, 0.7),
        transform: 'translateY(-1px)',
      },
      '&.Mui-focused': {
        background: alpha(theme.palette.background.paper, 0.8),
        transform: 'translateY(-1px)',
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontWeight: 500,
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: alpha(theme.palette.divider, 0.3),
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.5),
        borderWidth: '1px',
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
      },
    },
  },
}));

// Icon container for dialog headers
const IconHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginLeft: theme.spacing(-0.5),
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
    fontSize: '1.75rem',
    padding: theme.spacing(0.5),
    borderRadius: '8px',
    background: alpha(theme.palette.primary.main, 0.1),
    transition: 'all 0.2s ease',
    flexShrink: 0,
  },
}));

/**
 * Design System Dialog component that provides consistent styling across all dialogs
 */
const DesignSystemDialog = (props) => {
  const {
    open,
    onClose,
    title,
    icon,
    children,
    actions,
    maxWidth = 'sm',
    warning = false,
    contentProps = {},
    titleProps = {},
    actionsProps = {},
    PaperProps = {},
    BackdropProps = {},
    container,
    ...otherProps
  } = props;

  // Merge PaperProps styles properly
  const mergedPaperProps = {
    ...PaperProps,
    style: {
      ...(warning ? { border: '2px solid var(--error)' } : {}),
      ...PaperProps.style,
    },
    sx: {
      ...PaperProps.sx,
    },
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      container={container}
      PaperProps={mergedPaperProps}
      BackdropProps={BackdropProps}
      {...otherProps}
    >
      {title && (
        <DialogTitle {...titleProps}>
          {icon ? (
            <IconHeaderContainer>
              {icon}
              <span>{title}</span>
            </IconHeaderContainer>
          ) : (
            title
          )}
        </DialogTitle>
      )}
      
      <StyledDialogContent {...contentProps}>
        {children}
      </StyledDialogContent>
      
      {actions && (
        <DialogActions {...actionsProps}>
          {actions}
        </DialogActions>
      )}
    </StyledDialog>
  );
};

export default DesignSystemDialog; 